import React, {
  PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { PRICE_LADDER_CARD_ID } from "testIds";
import { getPanelShadow, getNewLookTokens } from "common/NewLook/tokens.ts";

interface TradeBarHoverCardProps {
  anchor: HTMLElement | null;
  x: number;
}

interface CardPosition {
  left: number;
  top: number;
  caret: number;
  isAbove: boolean;
}

const TradeBarHoverCard = ({
  anchor,
  x,
  children,
}: PropsWithChildren<TradeBarHoverCardProps>) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<CardPosition>();
  useLayoutEffect(() => {
    if (anchor && cardRef.current)
      setPosition(getCardPosition(anchor, cardRef.current, x));
  }, [anchor, x, children]);
  return createPortal(
    <Card
      ref={cardRef}
      role="tooltip"
      data-testid={PRICE_LADDER_CARD_ID}
      style={
        position
          ? { left: position.left, top: position.top }
          : { visibility: "hidden" }
      }
    >
      {position && (
        <Caret $isAbove={position.isAbove} style={{ left: position.caret }} />
      )}
      {children}
    </Card>,
    document.body
  );
};

export default TradeBarHoverCard;

const getCardPosition = (
  anchor: HTMLElement,
  card: HTMLElement,
  x: number
): CardPosition => {
  const bar = anchor.getBoundingClientRect();
  const { offsetWidth: width, offsetHeight: height } = card;
  const pointX = bar.left + x;
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(window.innerWidth - width - VIEWPORT_MARGIN, pointX - width / 2)
  );
  const isAbove =
    bar.bottom + GAP + height > window.innerHeight - VIEWPORT_MARGIN &&
    bar.top - GAP - height > VIEWPORT_MARGIN;
  return {
    left,
    top: isAbove ? bar.top - GAP - height : bar.bottom + GAP,
    caret: Math.max(CARET_EDGE, Math.min(width - CARET_EDGE, pointX - left)),
    isAbove,
  };
};

const VIEWPORT_MARGIN = 12;
const GAP = 10;
const CARET_EDGE = 18;
const CARD_WIDTH = 320;
const CARD_Z_INDEX = 1100;

const appear = keyframes`
  from { opacity: 0; transform: translateY(-3px); }
  to { opacity: 1; transform: none; }
`;
const Card = styled.div`
  position: fixed;
  z-index: ${CARD_Z_INDEX};
  width: ${CARD_WIDTH}px;
  max-width: calc(100vw - ${VIEWPORT_MARGIN * 2}px);
  padding: 18px 18px 12px;
  border-radius: 14px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) => getPanelShadow(p.theme)},
    0 12px 32px -12px rgba(0, 0, 0, ${(p) => (p.theme.isDarkMode ? 0.6 : 0.22)});
  pointer-events: none;
  animation: ${appear} 120ms ease-out;
`;
const Caret = styled.span<{ $isAbove: boolean }>`
  position: absolute;
  ${(p) => (p.$isAbove ? "bottom: -5px;" : "top: -5px;")}
  width: 10px;
  height: 10px;
  margin-left: -5px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) =>
    p.$isAbove
      ? `1px 1px 0 0 ${getNewLookTokens(p.theme).hairline}`
      : `-1px -1px 0 0 ${getNewLookTokens(p.theme).hairline}`};
  transform: rotate(45deg);
`;
